<template>
  <div class="search-block" :class="{ focused: isFocused }">
    <input
      ref="searchInput"
      v-model="searchTerm"
      type="text"
      :placeholder="$tr('vacancies.placeholders.vacancies')"
      class="search-block__area"
      autocomplete="off"
      @focus="focusSearch"
      @keyup.esc="blurSearchInput"
      @blur="hideSearchResults"
    />

    <button type="submit" class="search-block__find" @click="redirectToFullSearch">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M8.19909 0C5.05421 0 2.49571 2.55854 2.49571 5.70338C2.49571 7.20652 3.08051 8.57544 4.03417 9.59531L0.0976562 13.5318L0.565791 14L4.51356 10.0522C5.50831 10.8966 6.79513 11.4068 8.19905 11.4068C11.3439 11.4068 13.9024 8.84822 13.9024 5.70338C13.9024 2.55854 11.3439 0 8.19909 0ZM8.19909 10.7447C5.41928 10.7447 3.15779 8.48319 3.15779 5.70338C3.15779 2.92357 5.41928 0.66208 8.19909 0.66208C10.9789 0.66208 13.2403 2.92357 13.2403 5.70338C13.2403 8.48319 10.9788 10.7447 8.19909 10.7447Z"
        />
      </svg>
    </button>

    <div v-if="loading" class="search-block__loading">
      <vpreloader size="32" width="2" />
    </div>

    <div v-else-if="showSearchResults && haveAnyResults" class="search-block__results">
      <template v-if="vacancies.length">
        <div class="search-block__result">
          <div class="search-block__results-title">{{ $tr('vacancies.title') }}</div>
          <ul class="search-block__results-items">
            <li
              v-for="vacancy in vacancies"
              :key="vacancy.id"
              class="search-block__results-item"
              :class="{ disable: isVacancyRoleReadOrScoring(vacancy) }"
            >
              <a :disabled="isVacancyRoleReadOrScoring(vacancy)" :title="vacancy.title" @click="go_to_vacancy(vacancy)">
                {{ vacancy.title }}
              </a>
            </li>
          </ul>
        </div>
      </template>

      <template v-if="responses.length">
        <div class="search-block__result">
          <div class="search-block__results-title">{{ $tr('responses.title') }}</div>
          <ul class="search-block__results-items">
            <li v-for="response in responses" :key="response.id" class="search-block__results-item">
              <router-link tag="a" :to="getResponseRoute(response)">
                <span>{{ getCandidateName(response) }}</span>
                <span class="search-block__results-items-email">({{ response.candidate.email }})</span>
              </router-link>
            </li>
          </ul>
        </div>
      </template>

      <template v-if="invites.length">
        <div class="search-block__result">
          <div class="search-block__results-title">{{ $tr('invites.title') }}</div>
          <ul class="search-block__results-items">
            <li
              v-for="invite in invites"
              :key="invite.id"
              class="search-block__results-item"
              :class="{ disable: invite.response.is_deleted }"
            >
              <router-link tag="a" :to="getResponseRoute(invite.response)" :disabled="invite.response.is_deleted">
                <span>{{ getCandidateName(invite) }}</span>
                <span class="search-block__results-items-email">({{ invite.candidate.email }})</span>
              </router-link>

              <span
                v-if="invite.response.is_deleted && !invite.response.is_deleted_by_admin"
                class="remove-label remove-label_static"
              >
                {{ $tr('response.removeLabel') }}
              </span>
              <span
                v-if="invite.response.is_deleted && invite.response.is_deleted_by_admin"
                class="remove-label remove-label_static"
              >
                {{ $tr('response.removeLabelByAdmin') }}
              </span>
            </li>
          </ul>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import debounce from 'debounce';
import { mapState, mapGetters, mapMutations } from 'vuex';

export default {
  name: 'SearchForm',

  data() {
    return {
      isFocused: false,
      showSearchResults: false
    };
  },

  computed: {
    ...mapState('search', ['term']),
    ...mapState('search/responses', { responses: 'responses', loadingVacancies: 'loading' }),
    ...mapState('search/vacancies', { vacancies: 'vacancies', loadingResponses: 'loading' }),
    ...mapState('search/invites', { invites: 'invites', loadingInvites: 'loading' }),
    ...mapGetters('locale', ['is_japan_locale']),

    searchTerm: {
      get() {
        return this.term;
      },

      set(term) {
        this.$store.commit('search/setTerm', term);
      }
    },

    haveAnyResults() {
      return this.vacancies.length || this.responses.length || this.invites.length;
    },

    loading() {
      return this.loadingInvites || this.loadingResponses || this.loadingVacancies;
    }
  },

  watch: {
    $route(to) {
      if (to.name === 'search') {
        return;
      }
      this.clearSearch();
    },

    searchTerm(val) {
      if (!val) {
        return;
      }
      this.search(val);
    }
  },

  methods: {
    ...mapMutations('search', ['prepareResultPage']),

    fetch: debounce(function (searchTerm) {
      this.$store.dispatch('search/vacancies/fetch', { searchTerm });
      this.$store.dispatch('search/invites/fetch', { searchTerm });
      this.$store.dispatch('search/responses/fetch', { searchTerm });
      this.showSearchResults = true;

      this.prepareResultPage({ searchTerm: this.searchTerm, watch: true });
      // this.resetSearchHints();
    }, 1000),

    search: debounce(function (val) {
      const value = val.trim();

      if (value.length > 0) {
        this.fetch(value);
      } else {
        this.$store.commit('search/vacancies/clear');
        this.$store.commit('search/invites/clear');
        this.$store.commit('search/responses/clear');
      }
    }, 1000),

    getVacancyRoute({ id }) {
      return {
        name: 'vacancy_basic',
        params: { id }
      };
    },

    go_to_vacancy(vacancy) {
      if (this.$route.name === 'vacancies') {
        this.$router.push({
          name: 'vacancy_basic',
          params: {
            id: vacancy.id
          }
        });
      } else {
        this.$router.push(
          {
            path: '/vacancies'
          },
          () => {
            this.$nextTick().then(() => {
              this.$router.push({
                name: 'vacancy_basic',
                params: {
                  id: vacancy.id
                }
              });
            });
          }
        );
      }
    },

    getResponseRoute({ id }) {
      return {
        name: 'response',
        params: { id }
      };
    },

    hideSearchResults() {
      this.isFocused = false;
      setTimeout(() => (this.showSearchResults = false), 300);
    },

    getCandidateName(model) {
      if (!model.candidate) {
        return '';
      }

      return !this.is_japan_locale
        ? [model.candidate.name, model.candidate.surname].join(' ')
        : [model.candidate.surname, model.candidate.name].join(' ');
    },

    redirectToFullSearch() {
      this.blurSearchInput();
      this.$router.push({ name: 'search' });
    },

    clearSearch() {
      this.$store.commit('search/vacancies/clear');
      this.$store.commit('search/invites/clear');
      this.$store.commit('search/responses/clear');
      this.searchTerm = '';
    },

    blurSearchInput() {
      this.$refs.searchInput.blur();
      // this.resetSearchHints();
    },

    isVacancyRoleReadOrScoring(vacancy) {
      if (!vacancy) {
        return false;
      }

      return vacancy.role === 'role_read' || vacancy.role === 'role_read_scoring';
    },

    focusSearch() {
      this.showSearchResults = true;
      this.isFocused = true;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';
@import '@/styles/fonts.scss';

.search-block {
  position: relative;
  width: 100%;

  &__area {
    @include mixin-text-main;
    width: 100%;
    height: 64px;
    padding: 0 25px 0 44px;
    color: $grey;
    border-radius: 0;
    border: none;
    border-left: 1px solid $grey-light;
  }

  &__find {
    position: absolute;
    top: 50%;
    left: 20px;
    margin-top: -7px;
    width: 14px;
    height: 14px;

    svg path {
      fill: $grey;
    }
  }

  &__loading {
    position: absolute;
    top: 65px;
    left: 0;
    box-shadow: 0px 14px 24px rgba(0, 0, 0, 0.07);
    background-color: #fff;
    width: 100%;
    padding: 50px;
  }

  &__results {
    position: absolute;
    top: 65px;
    left: 0;
    box-shadow: 0px 14px 24px rgba(0, 0, 0, 0.07);
    background-color: #fff;
    width: 100%;
    min-width: 250px;
    max-height: 70vh;
    overflow-y: auto;

    &.search-block__item {
      &.disable {
        cursor: not-allowed;
      }
    }

    .remove-label_static {
      position: static;
    }
  }

  &__result {
    padding: 20px 0;
    border-bottom: 1px solid $grey-light;

    &:last-child {
      border-bottom: none;
    }
  }

  &__results-title {
    @include mixin-text-header;
    color: $main-dark;
    padding: 0 20px;
    margin-bottom: 10px;
  }

  &__results-items {
    list-style: none;
    margin-bottom: 15px;
    padding-left: 0;

    &:last-of-type {
      margin-bottom: 0;
      padding-bottom: 0;
      border-bottom: none;
    }

    li {
      cursor: pointer;

      &:hover,
      &.active {
        background: lighten($grey-light, 12%);
        transition: all 0.3s;
      }

      &.disable {
        &:hover,
        &.active {
          background: transparent;
        }
      }
    }
  }

  &__results-item {
    background: #fff;
    transition: all 0.3s;

    a {
      @include mixin-text-main;
      display: block;
      width: 100%;
      padding: 5px 20px;
      color: $main-dark;
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      transition: color 0.3s;
    }
  }

  &.focused {
    .search-block__find svg path {
      fill: $main-dark;
    }

    .search-block__area {
      color: $main-dark;
    }
  }

  &__results-items-email {
    font-weight: 400;
    font-size: 14px;
    line-height: 1.5;
    color: #a5b0c2;
  }
}
</style>
